import React, { useState } from 'react';
import { User } from '../../services/authService';
import PageHeader from '../common/PageHeader';
import './HindiAlphabet.css';

interface HindiAlphabetProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

interface AlphabetLetter {
  character: string;
  transliteration: string;
  pronunciation: string;
}

interface VowelMatra {
  matra: string;
  transliteration: string;
  name: string;
}

interface Combination {
  character: string;
  transliteration: string;
  breakdown: string;
}

// Static Devanagari Alphabet Data
const DEVANAGARI_VOWELS: AlphabetLetter[] = [
  { character: 'अ', transliteration: 'a', pronunciation: 'like "u" in "but"' },
  { character: 'आ', transliteration: 'ā', pronunciation: 'like "a" in "father"' },
  { character: 'इ', transliteration: 'i', pronunciation: 'like "i" in "bit"' },
  { character: 'ई', transliteration: 'ī', pronunciation: 'like "ee" in "feet"' },
  { character: 'उ', transliteration: 'u', pronunciation: 'like "u" in "put"' },
  { character: 'ऊ', transliteration: 'ū', pronunciation: 'like "oo" in "boot"' },
  { character: 'ऋ', transliteration: 'ṛ', pronunciation: 'like "ri" in "risk"' },
  { character: 'ए', transliteration: 'e', pronunciation: 'like "ay" in "say"' },
  { character: 'ऐ', transliteration: 'ai', pronunciation: 'like "ai" in "aisle"' },
  { character: 'ओ', transliteration: 'o', pronunciation: 'like "o" in "go"' },
  { character: 'औ', transliteration: 'au', pronunciation: 'like "ow" in "cow"' },
  { character: 'अं', transliteration: 'aṃ', pronunciation: 'nasal sound' },
  { character: 'अः', transliteration: 'aḥ', pronunciation: 'aspirated sound' },
];

const DEVANAGARI_CONSONANTS: AlphabetLetter[] = [
  // क वर्ग (Velar)
  { character: 'क', transliteration: 'ka', pronunciation: 'like "k" in "kite"' },
  { character: 'ख', transliteration: 'kha', pronunciation: 'like "kh" in "blockhead"' },
  { character: 'ग', transliteration: 'ga', pronunciation: 'like "g" in "go"' },
  { character: 'घ', transliteration: 'gha', pronunciation: 'like "gh" in "doghouse"' },
  { character: 'ङ', transliteration: 'ṅa', pronunciation: 'like "ng" in "sing"' },
  // च वर्ग (Palatal)
  { character: 'च', transliteration: 'ca', pronunciation: 'like "ch" in "church"' },
  { character: 'छ', transliteration: 'cha', pronunciation: 'aspirated "ch"' },
  { character: 'ज', transliteration: 'ja', pronunciation: 'like "j" in "jump"' },
  { character: 'झ', transliteration: 'jha', pronunciation: 'aspirated "j"' },
  { character: 'ञ', transliteration: 'ña', pronunciation: 'like "ny" in "canyon"' },
  // ट वर्ग (Retroflex)
  { character: 'ट', transliteration: 'ṭa', pronunciation: 'retroflex "t"' },
  { character: 'ठ', transliteration: 'ṭha', pronunciation: 'aspirated retroflex "t"' },
  { character: 'ड', transliteration: 'ḍa', pronunciation: 'retroflex "d"' },
  { character: 'ढ', transliteration: 'ḍha', pronunciation: 'aspirated retroflex "d"' },
  { character: 'ण', transliteration: 'ṇa', pronunciation: 'retroflex "n"' },
  // त वर्ग (Dental)
  { character: 'त', transliteration: 'ta', pronunciation: 'like "t" in "table"' },
  { character: 'थ', transliteration: 'tha', pronunciation: 'aspirated "t"' },
  { character: 'द', transliteration: 'da', pronunciation: 'like "d" in "dog"' },
  { character: 'ध', transliteration: 'dha', pronunciation: 'aspirated "d"' },
  { character: 'न', transliteration: 'na', pronunciation: 'like "n" in "no"' },
  // प वर्ग (Labial)
  { character: 'प', transliteration: 'pa', pronunciation: 'like "p" in "pet"' },
  { character: 'फ', transliteration: 'pha', pronunciation: 'aspirated "p"' },
  { character: 'ब', transliteration: 'ba', pronunciation: 'like "b" in "ball"' },
  { character: 'भ', transliteration: 'bha', pronunciation: 'aspirated "b"' },
  { character: 'म', transliteration: 'ma', pronunciation: 'like "m" in "mother"' },
  // Other consonants
  { character: 'य', transliteration: 'ya', pronunciation: 'like "y" in "yes"' },
  { character: 'र', transliteration: 'ra', pronunciation: 'like "r" in "run"' },
  { character: 'ल', transliteration: 'la', pronunciation: 'like "l" in "love"' },
  { character: 'व', transliteration: 'va', pronunciation: 'like "v" in "voice"' },
  { character: 'श', transliteration: 'śa', pronunciation: 'like "sh" in "ship"' },
  { character: 'ष', transliteration: 'ṣa', pronunciation: 'retroflex "sh"' },
  { character: 'स', transliteration: 'sa', pronunciation: 'like "s" in "sun"' },
  { character: 'ह', transliteration: 'ha', pronunciation: 'like "h" in "hot"' },
];

// Vowel matras (dependent vowels)
const VOWEL_MATRAS: VowelMatra[] = [
  { matra: 'ि', transliteration: 'i', name: 'इ (i)' },
  { matra: 'ी', transliteration: 'ī', name: 'ई (ī)' },
  { matra: 'ु', transliteration: 'u', name: 'उ (u)' },
  { matra: 'ू', transliteration: 'ū', name: 'ऊ (ū)' },
  { matra: 'े', transliteration: 'e', name: 'ए (e)' },
  { matra: 'ै', transliteration: 'ai', name: 'ऐ (ai)' },
  { matra: 'ो', transliteration: 'o', name: 'ओ (o)' },
  { matra: 'ौ', transliteration: 'au', name: 'औ (au)' },
];

// Common conjuncts (samyuktakshar)
const CONJUNCTS: Combination[] = [
  { character: 'क्ष', transliteration: 'kṣa', breakdown: 'क + ष' },
  { character: 'त्र', transliteration: 'tra', breakdown: 'त + र' },
  { character: 'ज्ञ', transliteration: 'jña', breakdown: 'ज + ञ' },
  { character: 'द्य', transliteration: 'dya', breakdown: 'द + य' },
  { character: 'ह्य', transliteration: 'hya', breakdown: 'ह + य' },
  { character: 'श्र', transliteration: 'śra', breakdown: 'श + र' },
  { character: 'द्व', transliteration: 'dva', breakdown: 'द + व' },
  { character: 'त्व', transliteration: 'tva', breakdown: 'त + व' },
  { character: 'द्र', transliteration: 'dra', breakdown: 'द + र' },
  { character: 'प्र', transliteration: 'pra', breakdown: 'प + र' },
  { character: 'भ्र', transliteration: 'bhra', breakdown: 'भ + र' },
  { character: 'श्व', transliteration: 'śva', breakdown: 'श + व' },
];

// Generate consonant + matra combinations (using क as example)
const generateMatraCombinations = (): Combination[] => {
  const combinations: Combination[] = [];
  const exampleConsonant = DEVANAGARI_CONSONANTS[0]; // क
  const baseConsonant = exampleConsonant.transliteration.replace('a', ''); // 'k'
  
  VOWEL_MATRAS.forEach(matra => {
    // For ि (i), the matra comes before the consonant
    if (matra.matra === 'ि') {
      combinations.push({
        character: matra.matra + exampleConsonant.character,
        transliteration: baseConsonant + matra.transliteration, // 'ki'
        breakdown: `${exampleConsonant.character} + ${matra.matra}`
      });
    } else {
      combinations.push({
        character: exampleConsonant.character + matra.matra,
        transliteration: baseConsonant + matra.transliteration, // e.g., 'kī', 'ku', etc.
        breakdown: `${exampleConsonant.character} + ${matra.matra}`
      });
    }
  });
  
  return combinations;
};

const MATRA_COMBINATIONS = generateMatraCombinations();

const HindiAlphabet: React.FC<HindiAlphabetProps> = ({
  onToggleSidebar
}) => {
  const [showVowels, setShowVowels] = useState(true);
  const [showConsonants, setShowConsonants] = useState(true);
  const [showMatras, setShowMatras] = useState(true);
  const [showConjuncts, setShowConjuncts] = useState(true);

  return (
    <div className="hindi-alphabet-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <h1 className="page-header-center-title">
            Hindi Alphabet
          </h1>
        }
      />

      <div className="hindi-alphabet-content">
        {/* Vowels Section */}
        <div className="hindi-alphabet-section">
          <button
            className="hindi-alphabet-section-toggle"
            onClick={() => setShowVowels(!showVowels)}
          >
            <span className="hindi-alphabet-section-header">/vowels</span>
            <span className="hindi-alphabet-toggle-arrow">{showVowels ? '▼' : '▶'}</span>
          </button>
          {showVowels && (
            <div className="hindi-alphabet-grid">
              {DEVANAGARI_VOWELS.map((letter, index) => (
                <div key={`vowel-${index}`} className="hindi-alphabet-card">
                  <div className="hindi-alphabet-char">{letter.character}</div>
                  <div className="hindi-alphabet-trans">{letter.transliteration}</div>
                  <div className="hindi-alphabet-pronunciation">{letter.pronunciation}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Consonants Section */}
        <div className="hindi-alphabet-section">
          <button
            className="hindi-alphabet-section-toggle"
            onClick={() => setShowConsonants(!showConsonants)}
          >
            <span className="hindi-alphabet-section-header">/consonants</span>
            <span className="hindi-alphabet-toggle-arrow">{showConsonants ? '▼' : '▶'}</span>
          </button>
          {showConsonants && (
            <div className="hindi-alphabet-grid">
              {DEVANAGARI_CONSONANTS.map((letter, index) => (
                <div key={`consonant-${index}`} className="hindi-alphabet-card">
                  <div className="hindi-alphabet-char">{letter.character}</div>
                  <div className="hindi-alphabet-trans">{letter.transliteration}</div>
                  <div className="hindi-alphabet-pronunciation">{letter.pronunciation}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Matra Combinations Section */}
        <div className="hindi-alphabet-section">
          <button
            className="hindi-alphabet-section-toggle"
            onClick={() => setShowMatras(!showMatras)}
          >
            <span className="hindi-alphabet-section-header">/combinations</span>
            <span className="hindi-alphabet-section-subtitle">Consonant + Vowel Matras</span>
            <span className="hindi-alphabet-toggle-arrow">{showMatras ? '▼' : '▶'}</span>
          </button>
          {showMatras && (
            <div className="hindi-alphabet-combinations">
              <div className="hindi-alphabet-combinations-info">
                Examples with क (ka) + vowel matras:
              </div>
              <div className="hindi-alphabet-grid">
                {MATRA_COMBINATIONS.map((combination, index) => (
                  <div key={`matra-${index}`} className="hindi-alphabet-card hindi-alphabet-combination-card">
                    <div className="hindi-alphabet-char">{combination.character}</div>
                    <div className="hindi-alphabet-trans">{combination.transliteration}</div>
                    <div className="hindi-alphabet-breakdown">{combination.breakdown}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Conjuncts Section */}
        <div className="hindi-alphabet-section">
          <button
            className="hindi-alphabet-section-toggle"
            onClick={() => setShowConjuncts(!showConjuncts)}
          >
            <span className="hindi-alphabet-section-header">/conjuncts</span>
            <span className="hindi-alphabet-section-subtitle">Samyuktakshar</span>
            <span className="hindi-alphabet-toggle-arrow">{showConjuncts ? '▼' : '▶'}</span>
          </button>
          {showConjuncts && (
            <div className="hindi-alphabet-grid">
              {CONJUNCTS.map((conjunct, index) => (
                <div key={`conjunct-${index}`} className="hindi-alphabet-card hindi-alphabet-combination-card">
                  <div className="hindi-alphabet-char">{conjunct.character}</div>
                  <div className="hindi-alphabet-trans">{conjunct.transliteration}</div>
                  <div className="hindi-alphabet-breakdown">{conjunct.breakdown}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HindiAlphabet;

